/**
 * LAWD Local Timer Hook
 * 
 * Manages countdown timers locally using device monotonic clock.
 * Independent of server polling - only syncs every 45 minutes.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for local elapsed time tracking
 * Uses performance.now() for monotonic timing (immune to clock changes)
 */
export function useLocalElapsedTime(startTimestamp) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);
  const baseElapsedRef = useRef(0);

  useEffect(() => {
    if (!startTimestamp) {
      setElapsed(0);
      return;
    }

    // Calculate initial elapsed from timestamp
    const startTime = new Date(startTimestamp).getTime();
    const now = Date.now();
    baseElapsedRef.current = Math.max(0, Math.floor((now - startTime) / 1000));
    startRef.current = performance.now();
    setElapsed(baseElapsedRef.current);

    // Update every second using monotonic clock
    const interval = setInterval(() => {
      const monotonicElapsed = Math.floor((performance.now() - startRef.current) / 1000);
      setElapsed(baseElapsedRef.current + monotonicElapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTimestamp]);

  return elapsed;
}

/**
 * Hook for countdown timer (e.g., "Rest By" time)
 * Counts down from a target time
 */
export function useLocalCountdown(targetTimestamp) {
  const [remaining, setRemaining] = useState(null);
  const startRef = useRef(null);
  const baseRemainingRef = useRef(0);

  useEffect(() => {
    if (!targetTimestamp) {
      setRemaining(null);
      return;
    }

    // Calculate initial remaining from timestamp
    const targetTime = new Date(targetTimestamp).getTime();
    const now = Date.now();
    baseRemainingRef.current = Math.max(0, Math.floor((targetTime - now) / 1000));
    startRef.current = performance.now();
    setRemaining(baseRemainingRef.current);

    // Update every second using monotonic clock
    const interval = setInterval(() => {
      const monotonicElapsed = Math.floor((performance.now() - startRef.current) / 1000);
      const newRemaining = Math.max(0, baseRemainingRef.current - monotonicElapsed);
      setRemaining(newRemaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTimestamp]);

  return remaining;
}

/**
 * Hook for compliance timers with local calculation
 * Calculates work/rest thresholds locally without server polling
 */
export function useLocalComplianceTimers(driverState, currentSegment) {
  const [timers, setTimers] = useState({
    continuousWork: 0,
    work24h: 0,
    restBy: null,
    shadowThreshold: null,
    lawThreshold: null
  });

  const startRef = useRef(null);
  const baseTimersRef = useRef(null);

  useEffect(() => {
    if (!driverState) return;

    // Initialize base values from server state
    baseTimersRef.current = {
      continuousWork: driverState.continuous_work_seconds || 0,
      work24h: driverState.work_24h_seconds || 0,
      restBy: driverState.rest_by_timestamp,
      shadowThreshold: driverState.shadow_threshold_seconds,
      lawThreshold: driverState.law_threshold_seconds
    };
    startRef.current = performance.now();

    // Set initial values
    setTimers({ ...baseTimersRef.current });

    // If currently working, increment counters locally
    const isWorking = currentSegment?.segment_type === 'WORK' || 
                      driverState.current_segment?.segment_type === 'WORK';

    if (isWorking) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((performance.now() - startRef.current) / 1000);
        
        setTimers(prev => ({
          ...prev,
          continuousWork: baseTimersRef.current.continuousWork + elapsed,
          work24h: baseTimersRef.current.work24h + elapsed
        }));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [driverState, currentSegment?.segment_type]);

  return timers;
}

/**
 * Hook for adaptive countdown timer (e.g., Night Rest / Long Rest deadlines)
 * Same pattern as useLocalCountdown but with adaptive polling cadence:
 *   > 30 min remaining: ticks every 5 minutes
 *   ≤ 30 min remaining: ticks every 60 seconds (driver finding pullover spot)
 *   Overdue: ticks every 60 seconds
 * Returns remaining in SECONDS (negative when overdue).
 */
export function useAdaptiveCountdown(targetTimestamp) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!targetTimestamp) {
      setRemaining(null);
      return;
    }

    const calc = () => {
      const targetTime = new Date(targetTimestamp).getTime();
      return Math.floor((targetTime - Date.now()) / 1000);
    };

    // Immediate first render
    setRemaining(calc());

    let timeoutId;
    const tick = () => {
      const secs = calc();
      setRemaining(secs);
      // Adaptive: 60s when ≤30min or overdue, 5min otherwise
      const nextMs = secs <= 1800 ? 60_000 : 300_000;
      timeoutId = setTimeout(tick, nextMs);
    };

    // Schedule first tick based on current urgency
    const initialSecs = calc();
    const firstMs = initialSecs <= 1800 ? 60_000 : 300_000;
    timeoutId = setTimeout(tick, firstMs);

    return () => clearTimeout(timeoutId);
  }, [targetTimestamp]);

  return remaining;
}


/**
 * Format adaptive countdown seconds into display string + urgency tier
 * Returns { display, urgency, label }
 *   urgency: 'safe' | 'warning' | 'critical' | 'overdue'
 */
export function formatCountdown(remainingSeconds) {
  if (remainingSeconds === null || remainingSeconds === undefined) {
    return { display: '--', urgency: 'safe', label: '' };
  }

  const isOverdue = remainingSeconds <= 0;
  const absSec = Math.abs(remainingSeconds);
  const days = Math.floor(absSec / 86400);
  const hrs = Math.floor((absSec % 86400) / 3600);
  const mins = Math.floor((absSec % 3600) / 60);

  let display;
  let urgency;
  let label;

  if (isOverdue) {
    display = days > 0 ? `${days}d ${hrs}h` : hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    urgency = 'overdue';
    label = 'OVERDUE';
  } else if (absSec < 1800) { // < 30 min
    display = `${mins}m`;
    urgency = 'critical';
    label = 'Pull over soon';
  } else if (absSec < 14400) { // < 4h
    display = `${hrs}h ${mins}m`;
    urgency = 'warning';
    label = 'Remaining';
  } else if (days > 0) {
    display = `${days}d ${hrs}h`;
    urgency = 'safe';
    label = 'Remaining';
  } else {
    display = `${hrs}h ${mins}m`;
    urgency = 'safe';
    label = 'Remaining';
  }

  return { display, urgency, label };
}
export function useLocalSegmentState(initialSegment) {
  const [segment, setSegment] = useState(initialSegment);
  const [isPending, setIsPending] = useState(false);

  // Optimistic start segment
  const startSegment = useCallback((segmentType, vehicleId = null) => {
    const now = new Date();
    const localSegment = {
      segment_id: `local-${Date.now()}`,
      segment_type: segmentType,
      start_ts: now.toISOString(),
      end_ts: null,
      vehicle_id: vehicleId,
      isLocal: true
    };
    
    setSegment(localSegment);
    setIsPending(true);
    
    return localSegment;
  }, []);

  // Optimistic stop segment
  const stopSegment = useCallback(() => {
    const now = new Date();
    
    setSegment(prev => prev ? {
      ...prev,
      end_ts: now.toISOString()
    } : null);
    setIsPending(true);
    
    return now.toISOString();
  }, []);

  // Confirm sync completed
  const confirmSync = useCallback((serverSegment) => {
    if (serverSegment) {
      setSegment(serverSegment);
    }
    setIsPending(false);
  }, []);

  // Handle sync failure (revert)
  const revertSync = useCallback((previousSegment) => {
    setSegment(previousSegment);
    setIsPending(false);
  }, []);

  return {
    segment,
    isPending,
    startSegment,
    stopSegment,
    confirmSync,
    revertSync,
    setSegment
  };
}

/**
 * Format seconds to HH:MM:SS or MM:SS
 */
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '--:--';
  
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format seconds to human readable (e.g., "5h 15m")
 */
export function formatDurationHuman(seconds) {
  if (seconds === null || seconds === undefined) return '--';
  
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  
  if (hrs > 0) {
    return `${hrs}h ${mins}m`;
  }
  return `${mins}m`;
}
